import { InfoIcon } from '@carboncopyco/icons';
import {
  ColorHex,
  Popover,
  TextButton,
  TextCaption,
  notify,
} from '@carboncopyco/ui';
import type { Table } from '@tanstack/react-table';
import { useBoolean } from 'ahooks';
import { isObject } from 'lodash-es';
import { useContext, useState } from 'react';
import { RulesManagerContext } from '../../../../RulesManager';
import type { TableFieldPanelProps } from '../TableFieldPanel';
import { CreateSpreadsheetButton } from './CreateSpreadsheetButton';
import { useTableToolsContext } from './TableToolsContextProvider';
import { UpdateTableButton } from './UpdateTableButton';

type ExportToSpreadsheetProps = Pick<TableFieldPanelProps, 'onUpdateTable'> & {
  table: Table<Record<string, unknown>>;
};

export const ExportToSpreadsheet = ({
  table,
  onUpdateTable,
}: ExportToSpreadsheetProps) => {
  const { spreadsheetApi } = useTableToolsContext();
  const { onLearnMoreClick: openSpreadsheet } = useContext(RulesManagerContext);

  const [{ spreadsheetUrl, spreadsheetId }, setSpreadsheetMeta] = useState<{
    spreadsheetUrl: string | null;
    spreadsheetId: string | null;
  }>({
    spreadsheetUrl: null,
    spreadsheetId: null,
  });

  const [isLoadingSpreadsheet, setIsLoadingSpreadsheet] = useBoolean(false);
  const [isUpdating, setIsUpdating] = useBoolean(false);
  const [isPopoverOpen, setIsPopoverOpen] = useBoolean(false);

  const onExportToCSVClick = async () => {
    setIsLoadingSpreadsheet.setTrue();
    try {
      const columns = table.getAllColumns();
      const { flatRows } = table.getCoreRowModel();

      const headersValues = columns
        .filter(({ id }) => id !== 'rowActions')
        .map((column) => column.columnDef.header) as string[];

      const rowsValues = flatRows.map((row) => {
        return row
          .getAllCells()
          .filter((cell) => cell.column.id !== 'rowActions')
          .map((cell) => {
            const value = cell.getValue();
            if (isObject(value)) {
              return JSON.stringify(value);
            }
            return value;
          });
      }) as string[][];

      const { spreadsheetUrl, id } = await spreadsheetApi.createSpreadsheet({
        title: 'Demostack',
        values: [headersValues, ...rowsValues],
      });

      openSpreadsheet(spreadsheetUrl);

      setSpreadsheetMeta({
        spreadsheetUrl,
        spreadsheetId: id,
      });

      setIsPopoverOpen.setTrue();
    } catch (_error) {
      notify.error('Failed to create spreadsheet');
    } finally {
      setIsLoadingSpreadsheet.setFalse();
    }
  };

  const onReadFromCSVClick = async () => {
    try {
      const res = await spreadsheetApi.getSpreadsheetValues(spreadsheetId);
      const values = [...res.values];
      const columns = table.getAllColumns();
      values.shift();

      const accessorKeys = columns
        .filter(({ id }) => id !== 'rowActions')
        .map((column) => (column.columnDef as any).accessorKey);

      const newData = values.flatMap((row) => {
        return accessorKeys.reduce<Record<string, unknown>>((acc, key, idx) => {
          let value = row[idx] as any;

          // Convert JSON string to object.
          try {
            const parsed = JSON.parse(value);
            if (isObject(parsed)) {
              value = parsed;
            }
          } catch (_error) {}

          acc[key] = value;

          return acc;
        }, {});
      }) as Record<string, unknown>[];

      if (newData.length) {
        onUpdateTable(newData);
        notify.success('Table was updated');
      }
    } catch (_error) {
      notify.error('Changes failed to import');
    } finally {
      setIsUpdating.setFalse();
    }
  };

  return (
    <>
      <Popover
        asChild
        open={isPopoverOpen}
        onInteractOutside={setIsPopoverOpen.setFalse}
        side="top"
        align="start"
        arrow
        contentClass="user-select-none"
        interactive={false}
        width={200}
        triggerElement={
          <div>
            {!spreadsheetId ? (
              <CreateSpreadsheetButton
                isLoading={isLoadingSpreadsheet}
                onClick={onExportToCSVClick}
              />
            ) : (
              <UpdateTableButton
                isLoading={isUpdating}
                openSpreadsheet={() => openSpreadsheet(spreadsheetUrl)}
                onClick={() => {
                  setIsUpdating.setTrue();
                  onReadFromCSVClick();
                }}
              />
            )}
          </div>
        }
      >
        <div className="p-3 flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <InfoIcon
              size={16}
              color={ColorHex.secondary300}
            />
            <TextButton
              label="Update data"
              color="text-ds-natural-600"
            />
          </div>
          <div className="leading-ds-200">
            <TextCaption
              label="You need to import the data in order to apply your changes."
              color="text-ds-natural-400"
            />
          </div>
        </div>
      </Popover>
    </>
  );
};
